import { ImageMessage } from './ImageMessage';
import type { RegenerateOption } from './RegenerateOptions';
import type { SlotType } from '../utils/imageUtils';

type InlineImageState = {
  imageUrl: string;
  isSaved: boolean;
  isSaving: boolean;
};

type InlineImagesProps = {
  images: { alt: string; url: string }[];
  targetSlot: SlotType;
  inlineImageStates: Record<string, InlineImageState>;
  showRegenerateFor: string | null;
  onSave: (imageUrl: string) => void;
  onReject: (imageUrl: string) => void;
  onRegenerateSelect: (option: RegenerateOption, imageUrl: string) => void;
  onCustomPrompt: () => void;
  styles: Record<string, string>;
};

export function InlineImages({
  images,
  targetSlot,
  inlineImageStates,
  showRegenerateFor,
  onSave,
  onReject,
  onRegenerateSelect,
  onCustomPrompt,
  styles,
}: InlineImagesProps) {
  return (
    <>
      {images.map((img) => {
        const state = inlineImageStates[img.url];
        const regenerateKey = `inline-${img.url}`;

        if (showRegenerateFor === regenerateKey) {
          return (
            <div key={`regen-${img.url}`} className={styles.rejectConversation}>
              <p className={styles.rejectQuestion}>What would you like me to do instead?</p>
              <div className={styles.prompts}>
                <button
                  type="button"
                  className={styles.promptOption}
                  onClick={() =>
                    onRegenerateSelect(
                      {
                        id: 'retry',
                        label: 'Try Again',
                        icon: '🔄',
                        prompt: 'Generate a new variation with the same settings',
                      },
                      img.url
                    )
                  }
                >
                  <span className={styles.promptNumber}>1.</span> Generate a new version
                </button>
                <button
                  type="button"
                  className={styles.promptOption}
                  onClick={() =>
                    onRegenerateSelect(
                      {
                        id: 'style',
                        label: 'Different Style',
                        icon: '🖼️',
                        prompt: 'Generate with a completely different artistic style',
                      },
                      img.url
                    )
                  }
                >
                  <span className={styles.promptNumber}>2.</span> Try a different style
                </button>
                <button type="button" className={styles.promptOption} onClick={onCustomPrompt}>
                  <span className={styles.promptNumber}>3.</span> Let me describe what I want
                </button>
              </div>
              <p className={styles.rejectHint}>Or just type what you'd like...</p>
            </div>
          );
        }

        return (
          <ImageMessage
            key={`inline-img-${img.url}`}
            imageUrl={img.url}
            targetSlot={targetSlot}
            isSaved={state?.isSaved || false}
            isSaving={state?.isSaving || false}
            onSave={() => onSave(img.url)}
            onReject={() => onReject(img.url)}
            message={img.alt || `Here's the generated image:`}
          />
        );
      })}
    </>
  );
}
